import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { combineLatest, startWith, Subject, takeUntil } from 'rxjs';
import { OnboardingListTableData } from '../../../interfaces/onboarding-list-table.interface';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { LoadingService } from '../../../Services/Loading/loading-service.service';
import { ConfirmDialogComponent } from '../confirm-dialog-component/confirm-dialog-component.component';
import { UpdateOnboardingDialogComponent } from '../update-onboarding-dialog/update-onboarding-dialog.component';
import { AuthService } from '../../../Services/Auth/auth.service';

@Component({
  selector: 'app-onboarding-list',
  templateUrl: './onboarding-list.component.html',
  styleUrls: ['./onboarding-list.component.scss'],
  standalone: false,
})
export class OnboardingListComponent implements OnInit {
  onboardingListForm: FormGroup;
  displayedColumns: string[] = [
    'accountNumber',
    'accountName',
    'integrationName',
    'integrationId',
    'product',
    'anniversaryDate',
    'lastInvoiceDate',
    'actionIcons',
  ];
  searchColumns: string[] = [
    'searchAccount',
    'placeHolder',
    'searchIntegration',
    'placeHolder',
    'searchProduct',
    'placeHolder',
    'placeHolder',
    'placeHolder',
  ];

  dataSource = new MatTableDataSource<OnboardingListTableData>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  isOnboardingExternalUser!: boolean;
  isOnboardingInternalUser!: boolean;
  isExternalUserOnboarding!: boolean;
  isInternalUserOnboarding!: boolean;
  private unsubscribe$ = new Subject<void>();

  constructor(
    public backend: BackendAccessService,
    public loadingService: LoadingService,
    public dialog: MatDialog,
    private auth: AuthService
  ) {
    this.onboardingListForm = new FormGroup({
      searchAccountControl: new FormControl(''),
      integrationSearchControl: new FormControl(''),
      categoryControl: new FormControl('AllProducts'),
    });
  }
  /* istanbul ignore next */
  ngOnInit() {
    this.auth.userProfile$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((userProfile) => {
        if (userProfile) {
          // Check if the user is internal
          this.isInternalUserOnboarding =
            userProfile['http://allegion.com/claims/user_type'] === 'internal';
          this.isExternalUserOnboarding =
            userProfile['http://allegion.com/claims/user_type'] === 'external';

          // Now, separately determine if they are a TechSupport role; this is irrespective of being internal or external
          const roles = userProfile['http://allegion.com/claims/roles'] || [];
          this.isOnboardingInternalUser =
            this.isInternalUserOnboarding &&
            (roles.includes('Onboarding') || roles.includes('InternalAdmin'));
          this.isOnboardingExternalUser =
            this.isExternalUserOnboarding &&
            (roles.includes('Onboarding') || roles.includes('InternalAdmin'));
        }
      });
    this.loadingService.show();

    // Fetch the onboarding list data
    this.backend.getOnboardingListData().subscribe(
      (data: OnboardingListTableData[]) => {
        this.dataSource.data = data;
        console.log('Onboarding List Data:', data);
        this.loadingService.hide();

        // Apply initial filter if any
        this.applyFilter();
      },
      (error) => {
        console.error('Error fetching onboarding list data:', error);
        this.loadingService.hide();
      }
    );

    combineLatest([
      this.onboardingListForm
        .get('integrationSearchControl')
        ?.valueChanges.pipe(startWith('')),
      this.onboardingListForm
        .get('searchAccountControl')
        ?.valueChanges.pipe(startWith('')),
      this.onboardingListForm
        .get('categoryControl')
        ?.valueChanges.pipe(startWith('')),
    ]).subscribe(() => {
      this.applyFilter();
    });
  }

  applyFilter() {
    const searchIntegration =
      this.onboardingListForm
        .get('integrationSearchControl')
        ?.value.trim()
        .toLowerCase() || '';
    const searchAccount =
      this.onboardingListForm
        .get('searchAccountControl')
        ?.value.trim()
        .toLowerCase() || '';

    // Assuming 'categoryControl' now holds a numerical value corresponding to the mappingType
    const selectedCategory =
      +this.onboardingListForm.get('categoryControl')?.value || 0;

    this.dataSource.filterPredicate = (
      data: OnboardingListTableData,
      filter: string
    ) => {
      const searchIntegration =
        this.onboardingListForm
          .get('integrationSearchControl')
          ?.value.trim()
          .toLowerCase() || '';
      const searchAccount =
        this.onboardingListForm
          .get('searchAccountControl')
          ?.value.trim()
          .toLowerCase() || '';
      const selectedCategory =
        +this.onboardingListForm.get('categoryControl')?.value || 0;

      const matchFilterIntegration = data.integrationName
        ? data.integrationName.toLowerCase().includes(searchIntegration)
        : false;
      const matchFilterAccount =
        (data.accountNumber
          ? data.accountNumber.toLowerCase().includes(searchAccount)
          : false) ||
        (data.accountName
          ? data.accountName.toLowerCase().includes(searchAccount)
          : false);

      // For 'All Products' (0), we assume it matches all types, so we ignore the filter
      const matchCategory =
        selectedCategory === 0 || data.mappingType === selectedCategory;

      return matchFilterIntegration && matchFilterAccount && matchCategory;
    };

    this.dataSource.filter = 'trigger-' + Date.now(); // Trigger filtering
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  editItem(itemData: any): void {
    console.log('Edit Item:', itemData);
    const dialogRef = this.dialog.open(UpdateOnboardingDialogComponent, {
      panelClass: 'update-onboarding-dialog',
      width: '90vw',
      maxWidth: '1200px',
      height: 'auto',
      maxHeight: '90vh',
      data: itemData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      this.reloadList();
    });
  }
  /* istanbul ignore next */
  reloadList(): void {
    this.loadingService.show();
    this.backend.getOnboardingListData().subscribe(
      (data: OnboardingListTableData[]) => {
        this.dataSource.data = data;
        this.loadingService.hide();
      },
      (error) => {
        console.error('Error fetching onboarding list data:', error);
        this.loadingService.hide();
      }
    );
  }

  /* istanbul ignore next */
  deleteItem(element: OnboardingListTableData) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
    });

    dialogRef.componentInstance.confirmed.subscribe(() => {
      if (!element.accountSiteMappingId) return;

      this.backend.deleteOnboardingData(element.accountSiteMappingId).subscribe({
        next: (response) => {
          if (response?.isSuccess === false) {
            dialogRef.componentInstance.errorMessage =
              'This site has active credentials and cannot be deleted.';
          } else {
            dialogRef.close();
            const index = this.dataSource.data.findIndex(
              (item) =>
                item.accountSiteMappingId === element.accountSiteMappingId
            );
            if (index !== -1) {
              this.dataSource.data.splice(index, 1);
              this.dataSource.data = [...this.dataSource.data];
            }
          }
        },
        error: (error) => {
          dialogRef.componentInstance.errorMessage =
            'An unexpected error occurred. Please try again.';
          console.error('Deletion error:', error);
        },
      });
    });
  }
}
